import { useEffect } from 'react';
import { toast } from 'react-toastify';

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiServiceProxy } from 'v2/services/api-proxy';

const SECURE_PROTOCOL = 'https:';

const isSecureUrl = (url: string) => {
  try {
    return new URL(url).protocol === SECURE_PROTOCOL;
  } catch {
    return false;
  }
};

export type DownloadFile = {
  id: number;
  name: string;
  extension: string;
  url_download: string;
};

export const useDownload = (hash: string) => {
  const listDownloads = useQuery({
    queryKey: ['downloadFile', hash],
    queryFn: async () => {
      const result = await apiServiceProxy.get<DownloadFile[]>(`/downloadfile/${hash}`);
      return result.data;
    },
    enabled: !!hash
  });

  useEffect(() => {
    if (listDownloads.isError) {
      toast.error('Ops, erro na listagem de downloads');
    }
  }, [listDownloads.isError]);

  const validateDownloadUrl = async (url: string): Promise<boolean> => {
    const fileHash = url.split('/').pop();
    const result = await apiServiceProxy.post('/validatedownloadhash', { hash: fileHash });
    return result.data?.status;
  };

  const getDownloadUrl = async (file: DownloadFile): Promise<string | null> => {
    const isValid = await validateDownloadUrl(file.url_download);

    if (isValid) {
      return file.url_download;
    }

    const refreshedDownloads = await listDownloads.refetch();

    if (refreshedDownloads.isError) {
      return null;
    }

    const refreshedFile = refreshedDownloads.data?.find(item => item.id === file.id);

    if (!refreshedFile) {
      return null;
    }

    const refreshedIsValid = await validateDownloadUrl(refreshedFile.url_download);

    if (!refreshedIsValid) {
      return null;
    }

    return refreshedFile.url_download;
  };

  const downloadFile = useMutation({
    mutationFn: async (file: DownloadFile) => {
      const url = await getDownloadUrl(file);

      if (!url || !isSecureUrl(url)) {
        toast.error('Link de download inválido');
        return;
      }

      // The download endpoint responds with Content-Disposition: attachment, so navigating keeps the page open.
      window.location.assign(url);
    }
  });

  return {
    downloadFile: (file: DownloadFile) => downloadFile.mutateAsync(file),
    isDownloading: listDownloads.isFetching,
    downloadingUrl: downloadFile.isPending ? downloadFile.variables?.url_download : undefined,
    downloads: listDownloads.data
  };
};
